'use client';
import { useEffect, useRef } from 'react';

interface Specialist {
  id: string;
  lat?: number;
  lng?: number;
  city?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  rating?: number;
  services?: { title: string }[];
}

interface SpecialistsMapProps {
  specialists: Specialist[];
  onSelect?: (id: string) => void;
}

declare global {
  interface Window { ymaps: any; }
}

export default function SpecialistsMap({ specialists, onSelect }: SpecialistsMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const YMAPS_KEY = process.env.NEXT_PUBLIC_YANDEX_MAPS_KEY;

  useEffect(() => {
    const withCoords = specialists.filter(s => s.lat && s.lng);

    const initMap = () => {
      if (!mapRef.current) return;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.destroy();
        mapInstanceRef.current = null;
      }

      // Центр — первый специалист с координатами или Москва
      const center = withCoords.length > 0
        ? [withCoords[0].lat, withCoords[0].lng]
        : [55.7558, 37.6173];

      const map = new window.ymaps.Map(mapRef.current, {
        center,
        zoom: withCoords.length === 1 ? 14 : 5,
        controls: ['zoomControl', 'geolocationControl'],
      });
      mapInstanceRef.current = map;

      // Добавляем метки
      withCoords.forEach(s => {
        const name = (s as any).user?.displayName || `${(s as any).user?.firstName || ''} ${(s as any).user?.lastName || ''}`.trim() || 'Специалист';
        const service = s.services?.[0]?.title || '';
        const ratingVal = parseFloat(String(s.rating || 0));
      const rating = ratingVal > 0 ? `★ ${ratingVal.toFixed(1)}` : '';

        const pm = new window.ymaps.Placemark(
          [s.lat, s.lng],
          {
            balloonContentHeader: `<strong>${name}</strong>`,
            balloonContentBody: [service, rating].filter(Boolean).join(' · '),
            balloonContentFooter: `<a href="/specialists/${s.id}" style="color:var(--color-brand);font-weight:600;">Перейти к профилю →</a>`,
            hintContent: name,
          },
          {
            preset: 'islands#redDotIconWithCaption',
            iconCaptionMaxWidth: '150',
          }
        );

        if (onSelect) {
          pm.events.add('click', () => onSelect(s.id));
        }

        map.geoObjects.add(pm);
      });

      // Подгоняем вид если несколько точек
      if (withCoords.length > 1) {
        map.setBounds(map.geoObjects.getBounds(), { checkZoomRange: true, zoomMargin: 40 });
      }
    };

    if (window.ymaps) {
      window.ymaps.ready(initMap);
    } else {
      const script = document.createElement('script');
      script.src = `https://api-maps.yandex.ru/2.1/?apikey=${YMAPS_KEY}&lang=ru_RU`;
      script.onload = () => window.ymaps.ready(initMap);
      document.head.appendChild(script);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.destroy();
        mapInstanceRef.current = null;
      }
    };
  }, [specialists]);

  const withCoords = specialists.filter(s => s.lat && s.lng);

  return (
    <div className="relative rounded-xl overflow-hidden border border-border" style={{ height: '600px' }}>
      <div ref={mapRef} className="w-full h-full" />
      {withCoords.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-bg-subtle">
          <p className="text-gray-400 text-sm">Нет специалистов с указанным местоположением</p>
        </div>
      )}
      <div className="absolute top-3 left-3 bg-bg-surface rounded-lg px-3 py-1.5 shadow text-xs text-text-secondary font-medium">
        {withCoords.length} из {specialists.length} специалистов на карте
      </div>
    </div>
  );
}
